import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	disconnectGmail,
	getGmailAuthUrl,
	getGmailStatus,
	syncGmailInbox,
} from '../services/integrations/gmailService';

export const GMAIL_KEY = ['gmail-connection'];

export const useGmailStatus = () =>
	useQuery({
		queryKey: GMAIL_KEY,
		queryFn: getGmailStatus,
		staleTime: 60 * 1000,
	});

/** Sends the browser to Google; it comes back to the current page with ?gmail=connected. */
export const useConnectGmail = () =>
	useMutation({
		mutationFn: async () => {
			const { url } = await getGmailAuthUrl(window.location.href);
			window.location.assign(url);
		},
	});

export const useDisconnectGmail = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: disconnectGmail,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: GMAIL_KEY }),
	});
};

/** Sync bounce/reply detection now; refresh Gmail status and outreach leads. */
export const useSyncGmailInbox = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: syncGmailInbox,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: GMAIL_KEY });
			queryClient.invalidateQueries({ queryKey: ['outreach-leads'] });
		},
	});
};

/** Shows the result of the Google redirect (?gmail=connected|error) once, then clears the params. */
export const useGmailCallbackToast = () => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();
	const result = searchParams.get('gmail');

	useEffect(() => {
		if (!result) return;
		if (result === 'connected') {
			toast({
				status: 'success',
				title: 'Gmail connected',
				description:
					'You can send outreach email and we can track bounces and replies.',
			});
			queryClient.invalidateQueries({ queryKey: GMAIL_KEY });
		} else {
			toast({
				status: 'error',
				title: 'Gmail connection failed',
				description: searchParams.get('gmailMessage') || undefined,
				isClosable: true,
			});
		}
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete('gmail');
				next.delete('gmailMessage');
				return next;
			},
			{ replace: true },
		);
	}, [result, searchParams, setSearchParams, toast, queryClient]);
};
